import React, { useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Link } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import AuthorizationChecker from '../../../services/AuthorizationChecker.js';
import useInfiniteLoadingLogic from '../../../hooks/useInfiniteLogic.jsx';
import DynamicTableInfinite from '../../widgets/dynamicTableInfinite/DynamicTableInfinite.jsx';
import UserDetailsLogs from '../userDetail/UserDetailsLogs.jsx';
import HelperMetiers360 from "../../../services/HelpersMetiers360.js";
import PropTypes from 'prop-types';
import { URLS } from '../../../services/axios';
import { useModal } from '../../../hooks/useModal.jsx';
import FormationSessionsByUserTableComponent from '../../formations/upload/formationSession/FormationSessionsByUserTableComponent.jsx';
import { useToast } from '../../../hooks/useToast.jsx';
import CreateUpdateUserComponent, { submitUserForm } from "../../users/CreateUpdateUserComponent";
import LogsComponent from '../../logs/LogsComponent.jsx';

const UserTableComponent = ({ users, full = false, isLoading = false, withParams = false,
    isShowRemoveEditBOAccessColumns = false }) => {
    const clientActionSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('clients'));
    const oneClientSlug = clientActionSlugs?.readOne ?? null;
    const { isParent } = useStoreState(state => state.auth);
    const { getIsParent } = useStoreActions(state => state.auth);

    const { fetchFormationSessionsByIdForUser, deleteUser, updateUser, setCreateFormErrors } = useStoreActions(actions => actions.users);

    const { apiData: { ACTIONS, BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);

    const { sharedSlugs } = useStoreState(actions => actions.params);

    const { modalComponent, setModalShow, modalData, setModalData, setIsSending } = useModal();
    const toast = useToast();

    const [sortedUsers, setSortedUsers] = useState([]);
    const [usersToDisplay, setUsersToDisplay] = useState([]);
    const [shouldSort, setShouldSort] = useState(false);

    useEffect(() => {
        if (isParent === null && !AuthorizationChecker.isAdmin()) {
            getIsParent();
        }
    }, []);

    useEffect(() => {
        setShouldSort(true)
        setSortedUsers([...users]);
    }, [users]);

    const {hasMore, fetchNext: fetchData} = useInfiniteLoadingLogic(sortedUsers, usersToDisplay, setUsersToDisplay, 100);

    if (!users.length) return <></>;

    const createDeleteModalBOAccess = (user) => {
        if(AuthorizationChecker.hasDeleteRights('users')) {
            setModalData({
                ...modalData,
                header: <>Retrait de l'accès BO</>,
                content: <h5>Êtes-vous sûr⋅e de vouloir retirer l'accès BO de <b>{user.email}</b>&nbsp;?</h5>,
                resetButton: 'Retirer',
                cancelButton: 'Annuler',
                onReset: () => {
                    setIsSending(true);
                    deleteUser({ uniqueId: user.uniqueId })
                        .then(() => {
                            toast.success()
                            setModalShow(false);
                        })
                        .finally(() => setIsSending(false));
                },
            });
            setModalShow(true);
        } 
    }

    const displayDeleteUserAccess = (user) => user?.playlistCount == 0 && !user?.isAdmin && !user?.isSuperUser
        ? <Button variant="danger" size="sm" className="m-0 p-2"
            onClick={()=>createDeleteModalBOAccess(user)}>
                <i className="fas fa-trash-alt"></i>
            </Button>
        : <OverlayTrigger
            placement="right"
            overlay={<Tooltip><div>L'accès utilisateur⋅rice ne peut pas être retiré car:
                <ul>
                    {user.playlistCount > 0 && <li>il⋅elle a des playlists</li>}
                    {user.isAdmin && <li>il⋅elle a un accès admin</li>}
                    {user.isSuperUser && <li>il⋅elle dispose de droits étendus</li>}
                </ul>
            </div></Tooltip>}>
            <span>
                <Button disabled variant="danger" size="sm" className="m-0 p-2">
                    <i className="fas fa-trash-alt"></i>
                </Button>
            </span>
        </OverlayTrigger>;


    const openEditUserModal = (user) => {
        if (AuthorizationChecker.hasUpdateRights('users')) {
            const validate = () => {
                setIsSending(true);
                const { formName } = ACTIONS['users']['update'];
                const emailList = users?.filter(u => u.uniqueId !== user.uniqueId)?.map(u => u.email);
                return submitUserForm(
                    updateUser,
                    formName,
                    user.uniqueId,
                    emailList,
                    emailList,
                    true,
                    true
                ).then(() => {
                    toast.success();
                    setModalShow(false);
                })
                    .catch((error) => {
                        if (error?.formError) {
                            setCreateFormErrors(error.formError);
                        }
                    })
                    .finally(() => setIsSending(false));
            }
            setModalData({
                ...modalData,
                header: <>Édition d'un utilisateur : {user.email}</>,
                content: <CreateUpdateUserComponent user={user} action="update" />,
                cancelButton: 'Annuler',
                validateButton: "Valider",
                onValidate: validate,
                size: 'lg'
            });
            setModalShow(true);
        }
    }

    const sortType = [
        {
            value : 'email', label : 'E-mail',
            test : HelperMetiers360.isArrayContainsValue(users, "email"),
            method: (a,b) => HelperMetiers360.localeCompareWithNullable(a.email, b.email),
            display: (user) => user.isSuperUser ? <>{user.email}
                <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>Cet utilisateur dispose de droits étendus</Tooltip>}>
                    <span>
                        <i className="fas fa-solid fa-star ml-2"></i>
                    </span>
                </OverlayTrigger>
            </>
                : user.email,
            flatDisplay: (user) => user.email + (user.isSuperUser ? " ★ (super utilisateur)" : "")
        },
        {
            value: 'lastName', label: "Nom",
            test : HelperMetiers360.isArrayContainsValue(users, "lastName"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.lastName, b.lastName),
            display: (user) => user.lastName ? user.lastName : '-'
        },
        {
            value: 'firstName', label: "Prénom",
            test : HelperMetiers360.isArrayContainsValue(users, "firstName"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.firstName, b.firstName),
            display: (user) => user.firstName ? user.firstName : '-'
        },
        {
            value: 'job', label: "Fonction",
            test: AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(users, "job"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.job, b.job),
            display: (user) => user.job && user.job !== "" ? user.job : '-'
        },
        {
            value: 'inviteTokenCreateDate', label: "Invitation",
            test : HelperMetiers360.isArrayContainsValue(users, "inviteTokenCreateDate"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.inviteTokenCreateDate, b.inviteTokenCreateDate),
            display: (user) => user.inviteTokenCreateDate ? HelperMetiers360.getdisplayDateType(user.inviteTokenCreateDate, 'day') : '-'
        },
        {
            value: 'lastLogin', label: "Dernière connexion",
            test: users.some(u => 'lastLogin' in u) && (isParent || AuthorizationChecker.isAdmin() || AuthorizationChecker.isSuperUser()),
            method: (a, b) => {
                if (a.lastLogin && b.lastLogin) return new Date(b.lastLogin) - new Date(a.lastLogin);
                if (!a.lastLogin && b.lastLogin)
                    return 1;
                if (a.lastLogin && !b.lastLogin)
                    return -1;
                if (!a.lastLogin && !b.lastLogin) {
                    const aLabel = a.inviteToken ? 'En attente':'Jamais';
                    const bLabel = b.inviteToken ? 'En attente':'Jamais';
                    return aLabel.localeCompare(bLabel);

                }
            },
            display: (user) => {
                if (user.lastLogin) return HelperMetiers360.getdisplayDateType(user.lastLogin, AuthorizationChecker.isAdmin() ? 'long' : 'day');
                if(user.inviteToken) return 'En attente';
                return 'Jamais';
            },
            getRedirectUrl: (user) => {
                if (!user.inviteToken)
                    return false;
                const url = URLS.BASE_URL + `/${sharedSlugs.activation}/${user.inviteToken}`;
                return url;
            },
            openInNewTab: true
        },
        {
            value: 'inviteToken', label: "Lien d'activation",
            test: HelperMetiers360.isArrayContainsValue(users, "inviteToken"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.inviteToken, b.inviteToken),
            display: (user) => {
                if (!user.inviteToken) return '-';
                return URLS.BASE_URL + `/${sharedSlugs.activation}/${user.inviteToken}`;
            },
            CSVOnly : true,
        },
        {
            value: 'allLogs', label: "Connexions",
            test: HelperMetiers360.isArrayContainsValue(users, "allLogs"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.allLogs, b.allLogs),
            display: (user) => {
            if(!user.allLogs) return '0';
                return ((isParent || AuthorizationChecker.isAdmin() || AuthorizationChecker.isSuperUser()) ?
                    <Button as="a" bsPrefix={'no-btn'} 
                onClick={() => {createLogsModal(user)}}>{user.allLogs ? user.allLogs : '0'}</Button>
                    : user.allLogs
                )
            },
            flatDisplay: (user) => user.allLogs ? user.allLogs : '0'
        },
        {
            value: 'logs', label: "Connexions au loader",
            test : HelperMetiers360.isArrayContainsValue(users, "logs"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.logs, b.logs),
            display: (user) => user.logs ? user.logs : '0'
        },
        {
            value: 'formations', label: "Inscriptions webinaires",
            test : HelperMetiers360.isArrayContainsValue(users, "formationSessionsCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.formationSessionsCount, b.formationSessionsCount),
            display: (user) => {
                if (!user.formationSessionsCount) return '0';
            return <Button as="a" bsPrefix={'no-btn'} 
                onClick={() => {createSessionsModal(user)}}>{user.formationSessionsCount}</Button>
            },
            flatDisplay: (user) => user.formationSessionsCount ?? "0",
        },
        {
            value: 'resetPasswordToken', label: "réinit mot de passe",
            test: HelperMetiers360.isArrayContainsValue(users, "resetPasswordToken"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.resetPasswordToken, b.resetPasswordToken),
            display: (user) => {
                if (user.resetPasswordToken) return 'En cours';
            },
            getRedirectUrl: (user) => {
                if (!user.resetPasswordToken)
                    return false;
                const url = URLS.BASE_URL + `/${sharedSlugs.reset}/${user.resetPasswordToken}`;
                return url;
            },
            flatDisplay: (user) => !user.resetPasswordToken ? '-' : URLS.BASE_URL + `/${sharedSlugs.reset}/${user.resetPasswordToken}`,
            openInNewTab: true
        },
        {
            value: 'clientBackoffice', label: "Back-office client",
            test: AuthorizationChecker.isAdmin(),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isAdmin, b.isAdmin, true),
            display: (user) => user.isAdmin ? '-' : <i className="fa fa-external-link-alt"></i>,
            flatDisplay: () => null,
            getRedirectUrl: (user) => {
                if (user.isAdmin)
                    return false;

                return URLS.API_URL + `/backoffice/?_switch_user=${user.email}`;
            }
        },
        {
            value: 'edit', label: "Editer",
            test: isShowRemoveEditBOAccessColumns && AuthorizationChecker.isSuperUser(),
            display: (user) => <Button variant="secondary" size="sm" className="m-0 p-2"
                onClick={() => openEditUserModal(user)}>
                <i className="fas fa-edit"></i>
            </Button>,
            flatDisplay: () => null
        },
        {
            value: 'delete', label: "Retrait accès BO",
            test: isShowRemoveEditBOAccessColumns && AuthorizationChecker.hasDeleteRights('users'),
            display: (user) => displayDeleteUserAccess(user),
            flatDisplay: () => null
        }        
    ]

    const createLogsModal = (user) => {
        setModalData({
            ...modalData,
            header: <>Logs de connexion de <b>{user.email}</b></>,
            content: <UserDetailsLogs user={user} />,  
            size: 'xl', 
        });
        setModalShow(true);
    }

    const createSessionsModal = (user) => {
        fetchFormationSessionsByIdForUser(user.uniqueId).then((res)=>{
            setModalData({
                ...modalData,
                header: <>Webinaires</>,
                content: <FormationSessionsByUserTableComponent formationSessionsList={res} isParent={isParent} />,  
                size: 'xl', 
            });
    
            setModalShow(true);
        })
    }

    if (full) {
        sortType.push(
            {
                value: 'clientName', label: "Établissement",
                test : HelperMetiers360.isArrayContainsValue(users, "clientName"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.clientName, b.clientName),
                display: (user) => {
                    if(!user.clientName || !user.clientUniqueId) return '-';
                    return user.clientUniqueId && oneClientSlug ?
                        <Link to={oneClientSlug.replace(':uniqueId', user.clientUniqueId)}>{user.clientName}</Link>
                        : <>{user.clientName}</>;
                },
                flatDisplay: (user) => user.clientName
            },
            {
                value: 'tagOperations', label: "Opé M360",
                test : HelperMetiers360.isArrayContainsValue(users, "tagOperations"),
                method: (a, b) => HelperMetiers360.sortStringArray(a.tagOperations?.map(tagOpe => tagOpe.name), b.tagOperations?.map(tagOpe => tagOpe.name)),
                display: (user) => user.tagOperations?.map(tagOpe => tagOpe.name)?.join(", ")
            },
            {
                value: 'categories', label: "Catégories",
                test : HelperMetiers360.isArrayContainsValue(users, "categories"),
                method: (a, b) => HelperMetiers360.sortStringArray(a.categories, b.categories),
                display: (user) => user.categories?.join(", ")
            },
            {
                value: 'status', label: "Statut",
                test: HelperMetiers360.isArrayContainsValue(users, "status"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.status, b.status),
                display: (user) => user.status
            },
            {
                value : 'subscriptionEndDate', label : 'Date de fin abo.',
                test : HelperMetiers360.isArrayContainsValue(users, "subscriptionEndDate"),
                method: (a, b) => new Date(b.subscriptionEndDate) - new Date(a.subscriptionEndDate),
                display: (user) => HelperMetiers360.getdisplayDateType(user.subscriptionEndDate, 'day')
            }
            );
    };

    if (AuthorizationChecker.isSuperUser() || AuthorizationChecker.isAdmin()) {
        sortType.push({
            value: 'actionLogs', label: "Logs",
            test: AuthorizationChecker.isSuperUser() || AuthorizationChecker.isAdmin(),
            display: (user) => <LogsComponent entityNameFromComponent="users" uniqueIdFromComponent={user.uniqueId} isUserLogs={true} />,
            flatDisplay: () => null
        })
    }

    return <>
        { usersToDisplay.length > 0 
            && <DynamicTableInfinite
                contentTable = {usersToDisplay}
                contentSort = {sortType}
                valueInitSort= 'lastLogin'
                index = 'uniqueId'
                fetchData={fetchData}
                hasMore={hasMore}
                setSortedContent={setSortedUsers}
                sortedContent={sortedUsers}
                sortState={[shouldSort, setShouldSort]}
                isLoading={isLoading}
            withParams={withParams}
                tableName="usersTable"
            filename="utilisateurices" />
        }
        {modalComponent}
    </>;
}

UserTableComponent.propTypes = {
    users: PropTypes.array.isRequired,
    full: PropTypes.bool,
    isLoading: PropTypes.bool,
    withParams: PropTypes.bool,
    isShowRemoveEditBOAccessColumns: PropTypes.bool,
}

export default UserTableComponent;